import React from 'react';
import compose from 'lodash/fp/compose';
import { useQuery } from '@apollo/client';
import { useFreightLasa } from '@catalogo/service-americanas-product/src/hooks/freight-lasa';
import { withCookies } from '@catalogo/core-connect/cookies';
import { withDevice } from '@catalogo/core-connect/device';
import { withMetrics } from '@catalogo/core-metrics';
import { withProductQuantity } from '@catalogo/service-americanas-product/src/hocs/product-quantity';
import {
  setInitialStore,
  fmtNearFreightOptions,
  handleDispatchMetrics,
} from '@catalogo/service-americanas-product/src/helpers/near-store';
import {
  getNearStoreServiceAcom,
  getSelectedStore,
} from '@catalogo/service-americanas-product/src/queries/store.graphql';
import { WithNearStoreProps, OfferType } from '../types';

export const withNearStore = (Component: React.FC) => (props: WithNearStoreProps) => {
  const { cookies, currentSku, dispatchMetrics, productQty } = props;
  const { persistentCep } = cookies;
  const offer = currentSku?.offers?.result?.[0] as OfferType;
  const sellerId = offer?.seller?.id;
  const skuId = offer?.sku;

  const { data: storeData, loading: storeLoading, error: storeError } = useQuery(getNearStoreServiceAcom, {
    variables: {
      cep: persistentCep,
      radius: 35,
      skuId,
      sellerId,
    },
    skip: !persistentCep || !skuId || !sellerId,
    ssr: false,
    onCompleted: setInitialStore,
    fetchPolicy: 'network-only',
    onError: setInitialStore,
  });

  const { data } = useQuery(getSelectedStore);

  const store = data?.selectedStore;

  const { loading: freightLoading, data: freightData, previousData: previousFreightData } = useFreightLasa({
    persistentCep,
    offer,
    onCompletedCallback: handleDispatchMetrics({ store, dispatchMetrics }),
    currentStore: store,
    productQty,
  });

  const isLoading = freightLoading || storeLoading;
  const currentFreight = freightData?.freight ?? previousFreightData?.freight;
  const freightIncentive = currentFreight?.options?.[0]?.products?.[0]?.freightIncetive;

  const fmtFreightData = {
    freight: {
      ...currentFreight,
      options: fmtNearFreightOptions(currentFreight),
      hasIncentiveForMoreCheapFreight: freightIncentive?.code === 'MORE_UNIT_FOR_CHEAPER_FREIGHT',
      hasIncentiveForFreeShipping: freightIncentive?.code === 'MORE_UNITS_TO_GET_FREE_SHIPPING',
      quantityForFreeShipping: freightIncentive?.quantity,
    },
  };

  const nearStoreProps = {
    store,
    storeData,
    storeError,
    isLoading,
    showLoader: isLoading && !currentFreight?.options,
    freightData: fmtFreightData,
    errorData: freightData?.freight?.error,
    isInvalidCep: freightData?.freight?.error?.errorCode === 'INVALID_CEP',
  };

  return <Component {...nearStoreProps} {...props} />;
};

export default compose(withCookies, withDevice, withMetrics, withProductQuantity, withNearStore);
